import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/data/site";

const primaryLinks = [
  { to: "/rooms", label: "Rooms" },
  { to: "/restaurant", label: "Dining" },
  { to: "/facilities", label: "Facilities" },
  { to: "/gallery", label: "Gallery" },
  { to: "/offers", label: "Offers" },
  { to: "/contact", label: "Contact" },
];

const menuGroups = [
  { title: "Discover", links: [["Home", "/"], ["About the Hotel", "/about"], ["Our Story", "/our-story"], ["Vision & Mission", "/vision-mission"], ["Meet Our Team", "/team"], ["Testimonials", "/testimonials"], ["FAQ", "/faq"]] },
  { title: "Stay", links: [["Rooms", "/rooms"], ["Superior Room", "/rooms/superior-room"], ["Standard Room", "/rooms/standard-room"], ["Room Amenities", "/amenities"]] },
  { title: "Dine & Unwind", links: [["Restaurant", "/restaurant"], ["Baobab Bar", "/bar-lounge"], ["Swimming Pool", "/facilities/swimming-pool"], ["Spa & Wellness", "/facilities/spa-wellness"]] },
  { title: "Plan", links: [["Book Your Stay", "/book"], ["Special Offers", "/offers"], ["Airport Transfers", "/facilities/airport-transfers"], ["Gallery", "/gallery"], ["Contact Us", "/contact"], ["Privacy & Terms", "/privacy-terms"]] },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${scrolled || open ? "bg-black/70 backdrop-blur-2xl backdrop-saturate-150 border-white/10" : "bg-gradient-to-b from-black/70 to-transparent border-transparent"}`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-[72px] md:h-[84px]">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src={LOGO_URL} alt="Zuri Sana Hotel" className="h-11 w-11 md:h-12 md:w-12 rounded-full object-cover ring-1 ring-[#d4af37]/50" />
            <span className="leading-tight">
              <span className="block font-serif-display text-lg md:text-xl tracking-[0.18em] text-white">ZURI SANA</span>
              <span className="block text-[10px] tracking-[0.4em] text-[#d4af37] uppercase">Hotel</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${isActive ? "text-[#d4af37]" : "text-white/70 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hidden md:flex items-center gap-2 text-white/70 hover:text-[#d4af37] text-sm transition-colors duration-300" data-testid="nav-call-btn">
              <Phone size={15} /> {CONTACT.phone}
            </a>
            <Link to="/book" className="btn-gold hidden sm:inline-block px-5 py-2.5 text-[12px] tracking-[0.16em] uppercase font-semibold" data-testid="nav-book-now-btn">
              Book Now
            </Link>
            <button
              onClick={() => setOpen(!open)}
              className="p-2 text-white hover:text-[#d4af37] transition-colors duration-300"
              aria-label="Menu"
              data-testid="nav-menu-toggle"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#0a0a0a]/97 backdrop-blur-xl overflow-y-auto pt-28 pb-16"
            data-testid="fullscreen-menu"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {menuGroups.map((group, gi) => (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + gi * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-5">{group.title}</p>
                  <ul className="space-y-3">
                    {group.links.map(([label, to]) => (
                      <li key={to + label}>
                        <Link
                          to={to}
                          className="font-serif-display text-xl text-white/75 hover:text-[#d4af37] hover:pl-2 transition-[color,padding] duration-300"
                          data-testid={`menu-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
            <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 pt-8 border-t hairline text-sm text-white/50">
              {CONTACT.address} · {CONTACT.phone} · {CONTACT.emails.info}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
